import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import { InfoHint } from '../../components/ui/infoHint';
import { IconTile } from '../../components/ui/Icon';
import type { IconName, IconTone } from '../../components/ui/Icon';
import './profileCards.css';

/**
 * Properties, the way a record shows them rather than the way a form does.
 *
 * ── The one change that does most of the work ──────────────────────────────
 *
 * `KeyValueRow` puts the label on the left and the value on the right, which
 * is what a settings form looks like. HubSpot stacks them, label above value,
 * and that is why theirs scans and ours reads as something to fill in. Kyu,
 * 14 September: "The way HubSpot presents the fields and properties is very
 * intuitive."
 *
 * ── Superseded, 15 September ──────────────────────────────────────────────
 *
 * This component now renders side by side after all: profileCards.css gives
 * .profile-prop a 92px label track. The paragraphs above are kept because the
 * reasoning still stands and the trade is real, not because the stack is what
 * ships.
 *
 * What changed is the complaint. Stacked, eight fields came to 470px of column
 * for about forty characters of information, and Kyu on 15 September: "The
 * management of the sizes of the boxes, the fonts, the fields. It's just not
 * aesthetically well thought of." The horizontal cost named below is still
 * paid, at about 176px of value track in the narrowest column. Short values
 * are the common case, so that is the cheaper side to pay on.
 *
 * Side by side also costs horizontal room we no longer have. The record column
 * is 300px at its narrowest (measured, see profileLayout.css); a label column
 * inside that leaves about 160px for the value, which is not enough for an
 * email address.
 *
 * ── An empty value is information ─────────────────────────────────────────
 *
 * It prints `--`, it does not disappear. Being able to see what is missing at
 * a glance is most of what makes a record feel informative, and a field that
 * hides when empty means the only way to learn it exists is to fill it in.
 */
/**
 * The label line: the words, then the hint mark if there is one.
 *
 * The words are in a span of their own rather than loose in the div. With the
 * hint button as a sibling text node, the label's textContent becomes "Nurture"
 * plus whatever glyph the mark draws, and anything selecting on the label —
 * a test, or a future style — stops matching the label it is looking for.
 */
function PropertyLabel({ label, explain }: { label: string; explain?: string | undefined }) {
  return (
    <div className="profile-prop-label">
      <span className="profile-prop-label-text">{label}</span>
      {explain === undefined ? null : <InfoHint label={label} text={explain} />}
    </div>
  );
}

export function Property({
  label,
  value,
  hint,
  explain,
}: {
  label: string;
  /**
   * The value. `null`, `undefined` and the empty string all print `--`.
   *
   * Deliberately not `string`: a value is often a pill, a link, or a date and
   * a copy button. What is NOT allowed is a caller deciding for itself what
   * empty looks like, which is how you end up with three spellings of nothing
   * in one column.
   */
  value: ReactNode;
  /** A short grey line under the value. The meaning, not the label again. */
  hint?: ReactNode;
  /**
   * Teaching copy, behind a `?` beside the label.
   *
   * Behind a mark rather than printed under the field, which is the rule
   * docs/design-rule.md sets: useful the first three opens, noise for the next
   * three hundred, and still one hover away. A sentence printed in full under
   * every field is how a column of four properties becomes a column of eight.
   */
  explain?: string | undefined;
}) {
  const empty = value === null || value === undefined || value === '';
  return (
    <div className="profile-prop">
      <PropertyLabel label={label} explain={explain} />
      <div className={`profile-prop-value${empty ? ' profile-prop-value--empty' : ''}`}>
        {empty ? '--' : value}
      </div>
      {hint === undefined ? null : <div className="profile-prop-hint">{hint}</div>}
    </div>
  );
}

/**
 * The same shape around a control that edits itself.
 *
 * Our properties are live selects and inputs, not read-only text with a click
 * to edit. This wrapper exists so an editable field sits in the same column
 * rhythm as a read-only one: label above, control below, hint under that.
 *
 * It does NOT render `--` for an empty control, because a control shows its
 * own emptiness — a select sitting on its placeholder is already saying it.
 * Printing `--` beside it would be two answers to one question.
 */
export function PropertyControl({
  label,
  hint,
  explain,
  children,
}: {
  label: string;
  hint?: ReactNode;
  /** Teaching copy, behind a `?` beside the label. See Property. */
  explain?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div className="profile-prop">
      <PropertyLabel label={label} explain={explain} />
      <div className="profile-prop-control">{children}</div>
      {hint === undefined ? null : <div className="profile-prop-hint">{hint}</div>}
    </div>
  );
}

/**
 * A named, collapsible group of properties.
 *
 * Not one flat list of every field. HubSpot's record shows "4ocean -
 * Partnership" and "About this contact" as separate cards with their own
 * headings and their own Actions menu, and that grouping is what makes forty
 * fields readable.
 *
 * ── Collapsed state is not persisted here ─────────────────────────────────
 *
 * The lead drawer keeps its fold state in a store so a section you closed
 * stays closed next time. That is the right behaviour and it stays the
 * caller's to own: this component takes `collapsed` and `onToggle` when the
 * caller has somewhere to keep them, and otherwise holds its own state for
 * the life of the mount. The alternative — this component reaching into a
 * store — would make it unusable in the deal and company profiles, which keep
 * their fold state somewhere else.
 */
type PropertyCardIcon =
  | {
      /** Optional as a pair. An unmapped card renders no blank or guessed tile. */
      icon: IconName;
      tone: IconTone;
    }
  | { icon?: undefined; tone?: undefined };

type PropertyCardProps = {
  title: string;
  /**
   * How many records this card holds, printed as "Deals (3)".
   *
   * The count is in the HEADING, not discovered by opening the card, because
   * that is the whole question a folded association card has to answer: is it
   * worth opening. Zero is a count and prints; undefined means this card is
   * not counting anything, which is what a property card is.
   */
  count?: number | undefined;
  /** Plain-language explanation, behind a hint mark beside the title. */
  hint?: string | undefined;
  /** The card's own menu, on the right of the heading. */
  actions?: ReactNode;
  /** Controlled fold state. Pass both, or neither. */
  collapsed?: boolean | undefined;
  onToggle?: (() => void) | undefined;
  defaultCollapsed?: boolean;
  /**
   * Cap the body and let it scroll on its own.
   *
   * For association cards only — the ones holding a list that can be any
   * length. A property card full of form fields never takes this: a scroller
   * over a set of inputs hides fields behind a gesture nobody expects there.
   *
   * Hanneza, 18 September, on a Deal Card: the Contacts heading said 9 and the
   * card showed 1, and "+ Add task" opened a form whose date field she could
   * not reach. The column around these cards is already a scroller; one long
   * list inside it is enough to push everything under it out of sight.
   *
   * The cap is in profileCards.css, at a height six rows clear, so the lists
   * people actually have keep exactly the behaviour they have now and only a
   * genuinely long one grows a second scrollbar.
   */
  scrolls?: boolean;
  children: ReactNode;
} & PropertyCardIcon;

export function PropertyCard({
  title,
  icon,
  tone,
  count,
  hint,
  actions,
  collapsed,
  onToggle,
  defaultCollapsed = false,
  scrolls = false,
  children,
}: PropertyCardProps) {
  const bodyId = useId();
  const [ownCollapsed, setOwnCollapsed] = useState(defaultCollapsed);
  const controlled = collapsed !== undefined && onToggle !== undefined;
  const isCollapsed = controlled ? collapsed : ownCollapsed;

  return (
    <section className="profile-card">
      <div className="profile-card-head">
        <button
          type="button"
          className="profile-card-toggle"
          aria-expanded={!isCollapsed}
          aria-controls={bodyId}
          onClick={() => {
            if (controlled) {
              onToggle();
            } else {
              setOwnCollapsed((was) => !was);
            }
          }}
        >
          <span aria-hidden="true" className="profile-card-caret">
            {isCollapsed ? '▸' : '▾'}
          </span>
          {icon === undefined || tone === undefined ? null : (
            <IconTile name={icon} tone={tone} className="profile-card-icon" />
          )}
          <span className="profile-card-title">{title}</span>
          {count === undefined ? null : <span className="profile-card-count">({count})</span>}
        </button>
        {/* Beside the toggle, never inside it. A button inside a button is
            invalid HTML and the inner click folds the card it was trying to
            explain — the same trap LeadPanelSection documents. */}
        {hint === undefined ? null : <InfoHint label={title} text={hint} />}
        {actions === undefined ? null : <div className="profile-card-actions">{actions}</div>}
      </div>
      {/* Unmounted rather than hidden, following LeadPanelSection: these cards
          hold live selects with their own autosave timers, and four of them
          kept mounted behind display:none cost exactly what they cost open. */}
      {isCollapsed ? null : (
        <div
          id={bodyId}
          className={`profile-card-body${scrolls ? ' profile-card-body--scrolls' : ''}`}
        >
          {children}
        </div>
      )}
    </section>
  );
}

/**
 * The body of an association card with nothing in it.
 *
 * It renders, it does not disappear. HubSpot draws "Deals (0)" with a mark and
 * one sentence saying what would be there, and that is deliberate: a record
 * with no deals and a record whose deal list you cannot see look identical if
 * the empty card is hidden. The zero is the answer.
 *
 * One sentence, in plain words, saying what this holds. Not "No results".
 */
export function ProfileEmptyCard({ children }: { children: ReactNode }) {
  return (
    <div className="profile-card-empty">
      <span aria-hidden="true" className="profile-card-empty-mark">
        ◎
      </span>
      <p>{children}</p>
    </div>
  );
}
